import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, doc, getDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, IconButton, Alert,
  CircularProgress, Checkbox, Divider, LinearProgress, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate, useParams } from 'react-router-dom';
import LagbasLogo from '../components/LagbasLogo';
import CompactSheet from '../components/cv/CompactSheet';
import CompactControls from '../components/cv/CompactControls';
import CompactAIDialog from '../components/cv/CompactAIDialog';
import { usePrintDocument } from '../hooks/usePrintDocument';
import { useCompactFit, useAutoFit } from '../hooks/useCompactFit';
import {
  DEFAULT_COMPACT_SETTINGS, buildCompactPerson, applyOverrides,
} from '../config/compactCV';
import { shortenForFit, diffSuggestion } from '../services/compactCVAI';

/**
 * Samlingsvy: alla anbudets konsulter i ETT kompakt dokument.
 * Dagens CV-vyer visar en person per sida – den här finns för att tre CV
 * ska kunna lämnas in som två A4 totalt.
 */
export default function TenderCVPrint() {
  const navigate = useNavigate();
  const { tenderId } = useParams();

  const [tender, setTender] = useState(null);
  const [rows, setRows] = useState([]);          // { userId, name, profile, roleInTender, variant }
  const [order, setOrder] = useState([]);        // userId[]
  const [selected, setSelected] = useState([]);  // userId[]
  const [company, setCompany] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_COMPACT_SETTINGS);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [error, setError] = useState('');

  const docRef = useRef(null);
  const handlePrint = usePrintDocument(docRef, {
    title: `Anbuds-CV – ${tender?.name || 'anbud'}`,
    margin: '8mm',
    compressImages: true,
  });

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true);
    const [tenderSnap, membersSnap, variantsSnap] = await Promise.all([
      getDoc(doc(db, 'tenders', tenderId)),
      getDocs(query(collection(db, 'tenderMembers'), where('tenderId', '==', tenderId))),
      getDocs(query(collection(db, 'cvVariants'), where('tenderId', '==', tenderId))),
    ]);

    const tenderData = { id: tenderSnap.id, ...tenderSnap.data() };
    setTender(tenderData);

    const variants = {};
    variantsSnap.docs.forEach(d => { variants[d.data().userId] = d.data(); });

    const members = membersSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(m => m.status !== 'declined');

    const loaded = await Promise.all(members.map(async member => {
      const [userSnap, profileSnap] = await Promise.all([
        getDoc(doc(db, 'users', member.userId)),
        getDoc(doc(db, 'profiles', member.userId)),
      ]);
      if (!profileSnap.exists()) return null;
      return {
        userId: member.userId,
        name: userSnap.data()?.name || userSnap.data()?.email || '',
        companyId: userSnap.data()?.companyId,
        status: member.status,
        roleInTender: member.suggestedRole || '',
        profile: profileSnap.data(),
        variant: variants[member.userId] || null,
      };
    }));

    const valid = loaded.filter(Boolean);
    setRows(valid);
    setOrder(valid.map(r => r.userId));

    const confirmed = valid.filter(r => r.status === 'confirmed').map(r => r.userId);
    setSelected(confirmed.length ? confirmed : valid.map(r => r.userId));

    const companyId = valid.find(r => r.companyId)?.companyId;
    if (companyId) {
      const compSnap = await getDoc(doc(db, 'companies', companyId));
      if (compSnap.exists()) setCompany(compSnap.data());
    }

    if (tenderData.compactCV) {
      setSettings({ ...DEFAULT_COMPACT_SETTINGS, ...tenderData.compactCV });
    }
    if (tenderData.compactCVOverrides) setOverrides(tenderData.compactCVOverrides);

    setLoading(false);
  }

  /* Sparar inställningar och nedkortningar på anbudet så att alla som öppnar
     vyn ser samma dokument – annars kan två personer skriva ut olika PDF:er. */
  useEffect(() => {
    if (loading) return undefined;
    const t = setTimeout(() => {
      const { autoNote, ...clean } = settings;
      updateDoc(doc(db, 'tenders', tenderId), {
        compactCV: clean, compactCVOverrides: overrides,
      }).catch(() => { /* tyst – utskriften ska inte blockeras av en skrivning */ });
    }, 900);
    return () => clearTimeout(t);
  }, [settings, overrides, loading, tenderId]);

  const persons = useMemo(() => (
    order
      .filter(id => selected.includes(id))
      .map(id => rows.find(r => r.userId === id))
      .filter(Boolean)
      .map(r => applyOverrides(
        buildCompactPerson({
          userId: r.userId,
          profile: r.profile,
          roleInTender: r.roleInTender,
          overrides: r.variant
            ? { ingress: r.variant.adjustedIngress, assignments: r.variant.adjustedAssignments }
            : null,
        }),
        overrides[r.userId],
      ))
  ), [rows, order, selected, overrides]);

  const editedFields = useMemo(() => {
    const flags = {};
    Object.entries(overrides).forEach(([userId, ov]) => {
      if (ov.ingress != null) flags[`${userId}:ingress`] = true;
      Object.entries(ov.assignments || {}).forEach(([index, patch]) => {
        Object.keys(patch).forEach(field => { flags[`${userId}:a${index}.${field}`] = true; });
      });
    });
    return flags;
  }, [overrides]);

  const fit = useCompactFit(docRef, settings, [persons, editedFields]);
  const setSettingsCb = useCallback(s => setSettings(s), []);
  useAutoFit(fit, settings, setSettingsCb);

  async function handleAIShorten() {
    setAiBusy(true);
    setError('');
    try {
      const result = await shortenForFit({
        persons,
        tender,
        overflowRows: Math.max(fit.overflowRows, 1),
        maxIngressChars: settings.maxIngressChars,
      });
      setAiResult({ note: result.note, changes: diffSuggestion(persons, result) });
    } catch (err) {
      console.error(err);
      setError(err.message || 'AI-nedkortningen misslyckades.');
    } finally {
      setAiBusy(false);
    }
  }

  function applyChanges(changes) {
    setOverrides(prev => {
      const next = { ...prev };
      changes.forEach(c => {
        const ov = next[c.userId] = { ...(next[c.userId] || {}) };
        if (c.key === 'ingress') { ov.ingress = c.to; return; }
        const [, rest] = c.key.split('.');
        const index = Number(c.key.slice(1).split('.')[0]);
        if (rest === 'drop') {
          ov.dropped = [...(ov.dropped || []), index];
          return;
        }
        ov.assignments = {
          ...(ov.assignments || {}),
          [index]: { ...((ov.assignments || {})[index] || {}), [rest]: c.to },
        };
      });
      return next;
    });
  }

  /** Fäller ut eller ihop beskrivningen för ett enskilt uppdrag. */
  function toggleExpand(userId, index) {
    setOverrides(prev => {
      const ov = { ...(prev[userId] || {}) };
      const list = ov.expanded || [];
      ov.expanded = list.includes(index)
        ? list.filter(i => i !== index)
        : [...list, index];
      return { ...prev, [userId]: ov };
    });
  }

  function move(userId, delta) {
    setOrder(prev => {
      const i = prev.indexOf(userId);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box className="no-print" sx={{ bgcolor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LagbasLogo />
            <Divider orientation="vertical" flexItem
              sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            <IconButton color="inherit"
              onClick={() => navigate('/tenders/' + tenderId + '/team')}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="bold">Anbuds-CV</Typography>
              <Typography variant="caption" sx={{ color: '#aaa' }}>
                {tender?.name} · {persons.length} konsulter i ett dokument
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {Object.keys(overrides).length > 0 && (
              <Button variant="outlined" startIcon={<RestartAltIcon />}
                onClick={() => setOverrides({})}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
                Återställ text
              </Button>
            )}
            <Tooltip title={fit.overflowRows > 0
              ? `Kortar ned text så att dokumentet krymper ${fit.overflowRows} rader`
              : 'Kortar ned text – dokumentet ryms redan'}>
              <span>
                <Button variant="outlined" startIcon={<AutoFixHighIcon />}
                  onClick={handleAIShorten} disabled={aiBusy || persons.length === 0}
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
                  {aiBusy ? 'Kortar ned…' : 'AI – korta ned'}
                </Button>
              </span>
            </Tooltip>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
              sx={{ bgcolor: 'white', color: '#1a1a1a' }}>
              Spara PDF
            </Button>
          </Box>
        </Box>
      </Box>

      <Container maxWidth={false} sx={{ py: 3 }}>
        {error && <Alert severity="error" className="no-print" sx={{ mb: 2 }}>{error}</Alert>}
        {aiBusy && <LinearProgress className="no-print" sx={{ mb: 2 }} />}

        <Paper className="no-print" variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
            Konsulter i dokumentet
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {order.map(userId => {
              const row = rows.find(r => r.userId === userId);
              if (!row) return null;
              return (
                <Box key={userId}
                  sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                  <Checkbox size="small" checked={selected.includes(userId)}
                    onChange={() => setSelected(s => s.includes(userId)
                      ? s.filter(x => x !== userId) : [...s, userId])} />
                  <Box>
                    <Typography variant="body2">{row.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {row.roleInTender || 'roll ej satt'}
                      {' · '}{row.profile.assignments?.length || 0} projekt
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => move(userId, -1)}>
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton size="small" onClick={() => move(userId, 1)}>
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Paper>

        <CompactControls
          mode="compact"
          showModeToggle={false}
          settings={settings}
          onSettingsChange={setSettings}
          persons={persons}
          fit={fit}
        />

        <CompactSheet
          docRef={docRef}
          persons={persons}
          settings={settings}
          company={company}
          docTitle="Anbuds-CV"
          subTitle={tender?.name}
          editedFields={editedFields}
          onToggleExpand={toggleExpand}
        />
      </Container>

      <CompactAIDialog
        open={!!aiResult}
        onClose={() => setAiResult(null)}
        changes={aiResult?.changes || []}
        note={aiResult?.note}
        onApply={applyChanges}
      />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0 !important; background: #fff !important; }
          .MuiContainer-root { padding: 0 !important; max-width: 100% !important; }
        }
      `}</style>
    </Box>
  );
}
